import type { WorkflowWorkersResponse } from '../types/workflow-inspection.types';

/** One worker on the task queue, with which kinds of task it polls for. */
export interface WorkerRow {
  identity: string;
  /** Latest poll across both lists. */
  lastAccessTime: string | null;
  workflowTaskHandler: boolean;
  activityHandler: boolean;
}

/** Workers tab data: the task queue plus one merged row per worker. */
export interface WorkersView {
  taskQueue: string | null;
  workers: WorkerRow[];
}

/**
 * Merge the separate workflow / activity poller lists into one row per worker
 * identity (Temporal's Workers table), most recently seen first. Must run on
 * RAW identities, before masking — masked, distinct workers on the same host
 * prefix (`1@integration-delivery-worker-••••`) would collapse into one row.
 */
export function mergePollers(data: WorkflowWorkersResponse): WorkersView {
  const byIdentity = new Map<string, WorkerRow>();
  const add = (identity: string, lastAccessTime: string | null, kind: 'workflow' | 'activity') => {
    const row = byIdentity.get(identity) ?? {
      identity,
      lastAccessTime: null,
      workflowTaskHandler: false,
      activityHandler: false,
    };
    if (kind === 'workflow') row.workflowTaskHandler = true;
    else row.activityHandler = true;
    // ISO-8601 UTC strings compare chronologically as plain strings.
    if (lastAccessTime && (!row.lastAccessTime || lastAccessTime > row.lastAccessTime)) {
      row.lastAccessTime = lastAccessTime;
    }
    byIdentity.set(identity, row);
  };
  for (const p of data.workflowPollers) add(p.identity, p.lastAccessTime, 'workflow');
  for (const p of data.activityPollers) add(p.identity, p.lastAccessTime, 'activity');
  const workers = [...byIdentity.values()].sort((a, b) =>
    (b.lastAccessTime ?? '').localeCompare(a.lastAccessTime ?? ''),
  );
  return { taskQueue: data.taskQueue, workers };
}
